import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../../utils/api";
import { Validation } from "../../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import { Member } from "../../../../../@types/member.types";
import JSONToExcel, { AOAtoExcel } from "../../../../../utils/commonFunctions/JSONToExcel";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { compareInvalidDates } from "../../../../../utils/commonFunctions/date";
import { Plan, PlanStatus } from "../../../../../@types/plan.types";
import { PLAN_STATUS } from "../../../../../constants/plan";
import { BENEFIT_CHANGES } from "../../../../../constants/benefitChangesConstant";
import { EMPLOYEE_INFO_CHANGES } from "../../../../../constants/employeeInfoChangesConstant";
import { useEmployerRoute } from "../../../../../hooks";
import { Group } from "../../../../../@types/group.types";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { ACTION_TAKEN } from "../../../../../constants/coreVEBActionTakenOrNotTakenConstant";
import { GROUP_NUMBER } from "../../../../../constants/groups";

const chipMessage = "";

const CoreActionTakenOrNotTakenReport = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [selectedPlan, setSelectedPlan] = useState<Plan>();

	const [plans, setPlans] = useState([]);

	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		alternative_report_name: "",
		group_number: null,
		type: "",
		action_taken: "",
		plan_code: ""
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, start_date, end_date, type, action_taken, plan_code } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		// employee validation checking
		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (plan_code.length === 0) {
			_validation.current.reportFieldDetails["plan_code"] = "Plan is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (action_taken.length === 0) {
			_validation.current.reportFieldDetails["action_taken"] = "Action Taken is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (type.length === 0) {
			_validation.current.reportFieldDetails["type"] = "Report Type is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// // console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const getPlans = useCallback(async () => {
		if (reportFieldDetails.group_number) {
			const { group_number } = reportFieldDetails;
			const planStatus: PlanStatus | string = PLAN_STATUS.active;
			const query = {
				status: planStatus,
				group_number
			};
			const _plans = await api.plan.getPlans(query);
			setPlans(Object.assign([], _plans));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reportFieldDetails.group_number]);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			if (name === "plan_code") {
				plans.forEach((plan: Plan) => {
					if (value === plan.plan_code) setSelectedPlan(Object.assign({}, plan));
				});

				setReportFieldDetails(Object.assign({}, reportFieldDetails, { plan_code: value }));
			} else setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[plans, reportFieldDetails]
	);

	// const handleDateChange = useCallback(
	// 	(_date: MaterialUiPickersDate, name: string) => {
	// 		const date = new Date(_date as Date);
	// 		const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
	// 		setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: parsedDate }));
	// 	},
	// 	[reportFieldDetails]
	// );

	const handleClearDateField = useCallback(
		(event: any, name: string) => {
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: null }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Core Action taken or not taken Report Generation");
					const { group_number, type, action_taken } = reportFieldDetails;

					let { plan_code } = reportFieldDetails;

					let sub_plan: string | null = null;
					if (group_number === GROUP_NUMBER.PANHANDLE_FIRST_BANK && plan_code.includes("Health Plan")) {
						// sub_plan = plan_code.split("(")[1].split(")")[0];

						const match = plan_code.match(/^([^(]+)\(([^)]+)\)$/);

						plan_code = match?.[1].trim() || ""; // "Health Plan"
						sub_plan = match?.[2].trim() || ""; // "High Deductible Health Plan"
					}

					const payload = {
						group_number,
						plan_name: plan_code,
						sub_plan: sub_plan,
						type,
						action_taken
					};

					// console.log("Payload", payload);
					const response: Member[] = await trackPromise(api.reports.coreActionTakenOrNotTakenReport(payload));
					// console.log("Response", response);
					if (response) {
						// console.log("Response", response);

						const fileName = reportFieldDetails.alternative_report_name
							? reportFieldDetails.alternative_report_name
							: "Core Action Taken or Not Taken for Report";
						delete reportFieldDetails["plan_code"];
						reportFieldDetails.plan_name = plan_code;
						const excelPayload = {
							user: {
								role: user?.role,
								name: user?.first_name + " " + user?.last_name
							},
							reportFieldDetails,
							response
						};

						await trackPromise(JSONToExcel(excelPayload, fileName));

						setTimeout(() => {
							setSnackbarAPICallProps(
								Object.assign({}, snackbarAPICallProps, {
									open: true,
									message:
										"Core Action Taken or Not Taken for Report Fetched Successfully and File Downloaded!",
									severity: "success"
								})
							);
						}, 2000);
					}
				}
			} catch (error) {
				// console.log("error", error);
				alert("Core Action Taken or Not Taken for Report Generation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails, snackbarAPICallProps, user]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.core_action_taken_or_not_taken_yoe_report);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		if (reportFieldDetails.start_date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { start_date: "" }));
		if (reportFieldDetails.end_date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { end_date: "" }));
	}, [reportFieldDetails]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Core Action Taken or Not Taken for YOE Report Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Plan",
							name: "plan_code",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.plan_code,
							type: "select",
							options: [...plans]
						},
						{
							label: "Type",
							name: "type",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.type,
							type: "select",
							options: ["ALL", "SUBMITTED", "WAIVED", "ACTIVE"]
						},
						{
							label: "Action Taken",
							name: "action_taken",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.action_taken,
							type: "select",
							options: ACTION_TAKEN
						},
						{
							label: "Alternative Report Name",
							name: "alternative_report_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Enter alternative report name",
							value: reportFieldDetails.alternative_report_name,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [
		assignedGroups,
		reportFieldDetails,
		handleChange,
		plans,
		isEmployerRoute,
		queryGroupNumber,
		selectedGroup?.name
	]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={handleClearDateField}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default CoreActionTakenOrNotTakenReport;
